import { useEffect, useState } from 'react';

import type { SelectedTravelQuote } from './selectedQuote';

/**
 * Vida local conservadora de la cotización: 15 minutos desde que la respuesta
 * fue aceptada. Pasado ese punto no se inicia el hold.
 */
export const FARE_TTL_MS = 15 * 60 * 1000;

/** Margen para avisar ANTES de que venza, con tiempo de terminar la carga. */
const EXPIRING_THRESHOLD_MS = 3 * 60 * 1000;

export type FareStatus = 'fresh' | 'expiring' | 'expired' | 'unknown';

export interface FareCountdown {
  status: FareStatus;
  remainingMs: number;
  /** "04:12" para mostrar; vacío si no se sabe cuándo se cotizó. */
  label: string;
  /**
   * Qué reloj fija el vencimiento que se está contando: `provider` cuando
   * manda el instante FIRMADO por el proveedor (`offerExpiresAt`), `local`
   * cuando rige la heurística del TTL. La UI lo usa para no atribuirle al
   * proveedor un plazo que es nuestro (ni al revés).
   */
  source: 'provider' | 'local';
  /**
   * El GATE del hold, derivado SOLO del TTL local. Es deliberadamente
   * distinto de `status`, que es ADVISORY (ver el doc de `fareCountdownAt`):
   * un reloj de navegador corrido no puede bloquear una oferta que el
   * proveedor todavía acepta. `false` ⇔ TTL local vencido o timestamp
   * desconocido.
   */
  freshForHold: boolean;
}

function format(remainingMs: number): string {
  const total = Math.max(0, Math.floor(remainingMs / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Cuánto le queda de vida a la tarifa cotizada.
 *
 * Sin esto el vencimiento es REACTIVO: el asesor carga los pasajeros, aprieta
 * reservar y recién ahí se entera de que la tarifa venció. El contador lo
 * convierte en algo que se ve venir.
 *
 * `quotedAt` es el `dataUpdatedAt` de la generación seleccionada: el momento
 * local en que React Query aceptó la respuesta, no un timestamp firmado por el
 * proveedor.
 *
 * `offerExpiresAt` es el vencimiento REAL que el proveedor firmó en la oferta
 * (contrato: `expiresAt` de la oferta de transfers, ya validado y parseado al
 * capturar la cotización — `SelectedTravelQuote.offerExpiresAt`). Alimenta el
 * DISPLAY (`status`/`remainingMs`/`source`): el vencimiento mostrado es el
 * MENOR de los dos relojes — el firmado adelanta el aviso cuando es más
 * temprano (expiry a +3 min, el caso que la heurística no veía) y no puede
 * alargar la vida del token de `provenance` (~15 min, lo que la heurística
 * aproxima). Sin el campo, la heurística queda sola — fallback explícito,
 * mismo comportamiento de siempre.
 *
 * ⚠️ El firmado es ADVISORY, nunca gate: es un instante ABSOLUTO de otro
 * dominio de reloj, y compararlo contra `Date.now()` hereda el corrimiento de
 * la workstation — un reloj adelantado marcaría vencida una oferta que el
 * proveedor todavía acepta, y como cada re-quote "vencería" igual, el asesor
 * no podría reservar nunca. El TTL local no tiene ese problema (`quotedAt` y
 * `now` salen del MISMO reloj), así que el gate del hold
 * (`freshForHold` / `isQuoteFreshForHold` / `canStartHoldForQuote` /
 * `canStartPreHold`) deriva SOLO de él. La enforcement del firmado es del
 * BACKEND: el hub rechaza pre-proveedor con `OFFER_EXPIRED`
 * (→ `RUTERO_OFFER_EXPIRED`, clean-terminal con CTA de re-cotizar). Con reloj
 * adelantado el peor caso es intentar, rechazo limpio y re-cotizar; con reloj
 * atrasado el contador muestra de más y el corte real lo pone el backend.
 *
 * Este es EL reloj (§21.9): countdown mostrado, gates de hold y el re-quote
 * de una búsqueda idéntica vencida derivan todos de esta función — display y
 * gate son dos FACETAS declaradas del mismo cómputo, no dos expresiones
 * parecidas repetidas.
 */
export function fareCountdownAt(
  quotedAt: number | undefined,
  now: number,
  offerExpiresAt?: number,
): FareCountdown {
  if (
    !Number.isFinite(quotedAt) ||
    (quotedAt ?? 0) <= 0 ||
    !Number.isFinite(now)
  ) {
    return {
      status: 'unknown',
      remainingMs: 0,
      label: '',
      source: 'local',
      freshForHold: false,
    };
  }

  const localExpiresAt = (quotedAt as number) + FARE_TTL_MS;
  if (!Number.isFinite(localExpiresAt)) {
    return {
      status: 'unknown',
      remainingMs: 0,
      label: '',
      source: 'local',
      freshForHold: false,
    };
  }

  // El instante firmado ya viene validado (captura); el isFinite es solo la
  // guarda del borde para un caller que arme el número por su cuenta.
  const signedExpiresAt =
    offerExpiresAt !== undefined && Number.isFinite(offerExpiresAt)
      ? offerExpiresAt
      : undefined;
  const providerGoverns =
    signedExpiresAt !== undefined && signedExpiresAt <= localExpiresAt;
  const displayExpiresAt = providerGoverns ? signedExpiresAt : localExpiresAt;

  const remainingMs = displayExpiresAt - now;
  const status: FareStatus =
    remainingMs <= 0
      ? 'expired'
      : remainingMs <= EXPIRING_THRESHOLD_MS
        ? 'expiring'
        : 'fresh';

  return {
    status,
    remainingMs,
    label: format(remainingMs),
    source: providerGoverns ? 'provider' : 'local',
    // Gate skew-inmune: SOLO el TTL local decide si el hold puede salir.
    freshForHold: localExpiresAt - now > 0,
  };
}

/**
 * El gate del hold: SOLO el TTL local (skew-inmune). El vencimiento firmado
 * no entra acá a propósito — es advisory; ver el doc de `fareCountdownAt`.
 */
export function isQuoteFreshForHold(
  quotedAt: number | undefined,
  now: number,
): boolean {
  return fareCountdownAt(quotedAt, now).freshForHold;
}

export function canStartHoldForQuote(
  quote: Pick<SelectedTravelQuote<unknown>, 'quotedAt'> | null,
  now: number,
): boolean {
  return Boolean(quote && isQuoteFreshForHold(quote.quotedAt, now));
}

export function useFareCountdown(
  quotedAt?: number,
  offerExpiresAt?: number,
): FareCountdown {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!Number.isFinite(quotedAt) || (quotedAt ?? 0) <= 0) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [quotedAt, offerExpiresAt]);

  return fareCountdownAt(quotedAt, now, offerExpiresAt);
}
